// Alle animaties die het dashboard kan afspelen, op één plek.
//
// id     - waar de CSS en de animatielaag naar kijken
// key    - toets waarmee je hem afspeelt (animatielab en dashboard)
// name   - hoe hij in het lab in de lijst staat
// hint   - wanneer hij in het echt te zien is
// ms     - hoe lang hij duurt (msFor: afhankelijk van de gegevens)
// target - "seller": licht ook de rij/trede van die verkoper op
// demo   - voorbeeldgegevens voor handmatig afspelen; play() mengt ze niet mee
// tour   - false: niet in de rondleiding

import { chargeMs } from "./charge";
import { missedSalesMs } from "./missedSales";
import { bestHoldMs, personalBestMs, recordBuildMs } from "./recordBuild";
import { targetChargeMs } from "./targetCharge";
import {
  partyBlocksMs,
  partyCannonMs,
  partyHypeMs,
  partyLightsMs,
  partyLineMs,
  partyMs,
  targetPartyMs,
  targetSongMs,
} from "./party";

// Gisteravond, zodat de demo van "Verkopen gemist" "gisteren" zegt.
const lastNight = Date.now() - 15 * 60 * 60 * 1000;

export const animations = [
  {
    id: "sale-charge",
    key: "c",
    name: "Opladen",
    hint: "De aanloop vlak voor een verkoop in beeld komt",
    // Tijden in ./charge.js; de aankondiging begint al eerder (chargeHandoff).
    ms: chargeMs,
  },
  {
    id: "sale-splash",
    key: "n",
    name: "Nieuwe verkoop",
    hint: "Het beeld gaat donker: wie, wat en hoeveel",
    // Hoort bij de tijden in animations.css; bij een record geeft saleGroup een eigen ms mee.
    ms: 9000,
    msFor: (payload) => payload.ms ?? 9000,
    demo: { name: "Ernst", amount: 1495, service: "Managed Cloud Backup" },
  },
  {
    id: "row-flash",
    key: "2",
    name: "Rij oplichten",
    hint: "Wijst aan wíe er verkocht heeft",
    ms: 1800,
    target: "seller",
    demo: { name: "Ernst" },
  },
  {
    id: "confetti-burst",
    key: "3",
    name: "Confetti",
    hint: "Grote deal of mijlpaal",
    // Laatste snipper: 1,6s wachten + 4,6s vallen, plus marge.
    ms: 6600,
  },
  {
    id: "personal-best",
    key: "4",
    name: "Eigen record",
    hint: "Iemands grootste verkoop tot nu toe",
    // Na een verkoop (continued) alleen het geluid, los afgespeeld de aankondiging als record.
    ms: personalBestMs,
    msFor: (payload) => (payload.continued ? bestHoldMs : personalBestMs),
    demo: {
      name: "Ernst",
      amount: 1850,
      service: "Managed Cloud Backup",
      previous: 1200,
    },
  },
  {
    id: "record-stamp",
    key: "x",
    name: "Record-stempel",
    hint: "Slaat op de aankondiging als de verkoop een record is",
    // Hoort bij de tijden in animations.css; het moment staat bij stampAfterSplash.
    ms: 1900,
    demo: { kind: "best" },
  },
  {
    // Alleen geluid, zie ./recordBuild.js.
    id: "record-build",
    key: "o",
    name: "Record: opbouw",
    hint: "Het geluid dat aanzwelt naar de record-stempel",
    ms: recordBuildMs,
    tour: false,
  },
  {
    id: "rank-up",
    key: "5",
    name: "Stijger",
    hint: "Iemand schuift op in de ranglijst",
    ms: 3600,
    demo: { name: "Ernst", position: 2, from: 4 },
  },
  {
    id: "gauge-pulse",
    key: "6",
    name: "Meter pulseert",
    hint: "Het teamtotaal loopt op",
    ms: 2400,
  },
  {
    id: "screen-glow",
    key: "7",
    name: "Schermrand",
    hint: "Het seintje voor een verkoop in beeld komt",
    // Daarna begint de aanloop meteen, zie glowLead in useSaleEvents.js.
    ms: 5000,
  },
  {
    id: "fireworks",
    key: "8",
    name: "Vuurwerk",
    hint: "Grootste losse verkoop ooit",
    // Langer dan de knallen, zodat de tekst te lezen is.
    ms: 7500,
    demo: {
      name: "Ernst",
      amount: 4250,
      service: "Managed Cloud Backup",
      previous: 3200,
    },
  },
  {
    id: "podium-bounce",
    key: "9",
    name: "Podium veert",
    hint: "De top 3 verandert van volgorde",
    ms: 1800,
  },
  {
    id: "target-charge",
    key: "t",
    name: "Meter naar 100",
    hint: "De aanloop naar het target: de wijzer loopt vol",
    // Tijden in ./targetCharge.js; "GEHAALD" begint al eerder (targetPartyGroup).
    ms: targetChargeMs,
  },
  {
    id: "target-hit",
    key: "0",
    name: "Target gehaald",
    hint: "Het team tikt de 100% aan",
    ms: 9500,
    demo: { amount: 85000 },
  },
  {
    id: "target-party-dim",
    key: "v",
    name: "Target: feestlicht",
    hint: "Het beeld zakt een tikje weg zolang het targetfeest duurt",
    // Van "GEHAALD" tot het nummer uit is.
    ms: targetPartyMs,
    tour: false,
  },
  {
    // Alleen geluid, zie targetPartyGroup in ./party.js.
    id: "target-song",
    key: "u",
    name: "Target: nummer",
    hint: "Het lange feestnummer onder het targetfeest",
    ms: targetSongMs,
    tour: false,
  },
  {
    id: "missed-sales",
    key: "m",
    name: "Verkopen gemist",
    hint: "Na het aanzetten: wat er verkocht is terwijl de tv uit stond",
    // ms is de duur van de demo; live rekent msFor met het aantal verkopen.
    ms: missedSalesMs(3),
    msFor: (payload) => missedSalesMs(payload.sales?.length ?? 0),
    demo: {
      since: lastNight,
      amount: 3525,
      sales: [
        { name: "Ernst", amount: 1495, service: "Managed Cloud Backup" },
        { name: "Bert", amount: 1250, service: "Microsoft 365" },
        { name: "Hans", amount: 780, service: "Werkplekbeheer" },
      ],
    },
  },
  {
    // Dezelfde kaart met een lege lijst, om los te bekijken; live komt dit uit "missed-sales".
    id: "missed-nothing",
    key: "l",
    name: "Niks gemist",
    hint: "Na het aanzetten: er is niets verkocht terwijl de tv uit stond",
    ms: missedSalesMs(0),
    demo: {
      since: lastNight,
      amount: 0,
      sales: [],
    },
  },
  {
    // De vijf feestanimaties spelen samen via partyGroup in ./party.js, en hier los voor het lab.
    id: "party-dance",
    key: "f",
    name: "Feest: dansen",
    hint: "Het hele dashboard gaat op de maat mee",
    // Zo lang als het nummer; het target geeft een eigen duur mee.
    ms: partyMs,
    msFor: (payload) => payload.ms ?? partyMs,
    tour: false,
  },
  {
    id: "party-blocks",
    key: "g",
    name: "Feest: banen",
    hint: "Vlakke banen kleur die over het beeld schuiven",
    ms: partyBlocksMs,
    tour: false,
  },
  {
    id: "party-hype",
    key: "d",
    name: "Feest: aanmoediging",
    hint: "De balk onderin die regels doorbladert",
    // Een beurt per regel; het target heeft een eigen lijst.
    ms: partyHypeMs,
    msFor: (payload) =>
      payload.lines ? payload.lines.length * partyLineMs : partyHypeMs,
    tour: false,
  },
  {
    id: "party-lights",
    key: "b",
    name: "Feest: lichtshow",
    hint: "Lichtbundels van boven die op de maat zwaaien",
    ms: partyLightsMs,
    msFor: (payload) => payload.ms ?? partyLightsMs,
    tour: false,
  },
  {
    id: "party-cannons",
    key: "k",
    name: "Feest: kanonnen",
    hint: "Confetti vanuit beide hoeken onderin omhoog geschoten",
    ms: partyCannonMs,
    tour: false,
  },
];

// De rondleiding (spatiebalk) knipt na drie seconden af, dus zonder de nummers.
export const tourAnimations = animations.filter((animation) => animation.tour !== false);

export const durationOf = (animation, payload = {}) =>
  animation.msFor ? animation.msFor(payload) : animation.ms;

export const byId = (id) => animations.find((a) => a.id === id) || null;
export const byKey = (key) => animations.find((a) => a.key === key) || null;
